import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
  fetchRosterForPoolAndDoctor,
  addToRoster,
  removeFromRoster,
} from '../../services/api/pool-manager';
import Days from '../AdminActions/Days.json';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const WeekContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const DayRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 10px;
  margin-top: 6px;
  background-color: #fafafa;
  border: 0.1px #cecece solid;
  border-radius: 3px;
`;

const DayLabel = styled(BoldText)`
  width: 8rem;
  flex-shrink: 0;
`;

const SlotsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Slot = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.85rem;
  padding: 2px 8px 2px 10px;
  margin: 2px 6px 2px 0;
  border-radius: 10px;
  color: #4b2e83;
  background-color: #f0e9fa;
`;

const RemoveSlotButton = styled.button`
  border: none;
  background: transparent;
  color: #a1170f;
  cursor: pointer;
  font-size: 0.95rem;
  line-height: 1;
  margin-left: 6px;
  padding: 0 2px;
  :disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const EmptySlot = styled(RegularText)`
  color: #aaa;
`;

const HelperText = styled(RegularText)`
  color: #888;
  margin-top: 10px;
`;

const AddForm = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed #d5d5d5;
`;

const Select = styled.select`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 6px 8px;
  margin: 4px 8px 4px 0;
  border: 1px solid #d5d5d5;
  border-radius: 0.375rem;
  font-size: 0.85rem;
`;

const AddButton = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 7px 16px;
  margin: 4px 0;
  border: none;
  border-radius: 10px;
  color: white;
  background: linear-gradient(135deg, #6b4bd6, #4b2e83);
  box-shadow: 0 6px 14px rgba(75, 46, 131, 0.22);
  cursor: pointer;
  :disabled {
    background: #c5c0d4;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

// Days 1..7 map to Monday..Sunday. Value 8 ("All Days") is a shortcut in the
// add-slot dropdown that fans the slot out across every weekday (1..7).
const ALL_DAYS_VALUE = 8;
const WEEK_DAYS = Days.filter((d) => d.value >= 1 && d.value <= 7);
// Dropdown options for the add form: real weekdays + the "All Days" shortcut.
const DAY_OPTIONS = Days.filter((d) => d.value >= 1 && d.value <= ALL_DAYS_VALUE);
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const formatHour = (hour) => {
  const h = parseInt(hour, 10);
  if (Number.isNaN(h)) {
    return `${hour}`;
  }
  const suffix = h >= 12 ? 'PM' : 'AM';
  let display = h % 12;
  if (display === 0) {
    display = 12;
  }
  return `${display} ${suffix}`;
};

class PoolRosterView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      roster: [],
      loading: false,
      error: null,
      saving: false,
      newDay: WEEK_DAYS.length ? WEEK_DAYS[0].value : 1,
      newStartHour: 9,
      newEndHour: 10,
    };
  }

  componentDidMount() {
    this.getRoster();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.doctorId !== this.props.doctorId ||
      prevProps.poolId !== this.props.poolId
    ) {
      this.getRoster();
    }
  }

  getRoster = () => {
    const { doctorId, poolId } = this.props;
    if (!doctorId || !poolId) {
      return;
    }
    this.setState({ loading: true, error: null });
    fetchRosterForPoolAndDoctor(doctorId, poolId)
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ roster: res.roster || [] });
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to fetch roster');
        }
      })
      .catch((err) => {
        const message = err.message || 'Failed to fetch roster';
        toast.error(message);
        this.setState({ roster: [], error: message });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  onFieldChange = (field) => (e) => {
    this.setState({ [field]: parseInt(e.target.value, 10) });
  };

  onAddSlot = () => {
    const { doctorId, poolId } = this.props;
    const { newDay, newStartHour, newEndHour, roster } = this.state;

    if (newEndHour <= newStartHour) {
      toast.error('End hour must be greater than start hour.');
      return;
    }

    // "All Days" fans the slot out across every weekday (1..7); a single day
    // stays a one-row add.
    const isAllDays = Number(newDay) === ALL_DAYS_VALUE;
    const candidateDays = isAllDays
      ? WEEK_DAYS.map((d) => d.value)
      : [Number(newDay)];

    // Skip days that already have an identical slot (same start+end) so the
    // All-Days shortcut doesn't create duplicate roster rows.
    const targetDays = candidateDays.filter(
      (day) =>
        !roster.some(
          (s) =>
            parseInt(s.day, 10) === day &&
            Number(s.startHour) === Number(newStartHour) &&
            Number(s.endHour) === Number(newEndHour)
        )
    );

    if (targetDays.length === 0) {
      toast.info('That slot already exists for the selected day(s).');
      return;
    }

    this.setState({ saving: true });
    Promise.all(
      targetDays.map((day) =>
        addToRoster(
          { day, startHour: newStartHour, endHour: newEndHour },
          doctorId,
          poolId
        )
          .then((res) => (res && res.message === 'success' ? true : false))
          .catch(() => false)
      )
    )
      .then((results) => {
        const added = results.filter(Boolean).length;
        const failed = results.length - added;
        if (added && !failed) {
          toast.success(
            isAllDays
              ? `Roster slot added for ${added} day${added > 1 ? 's' : ''}.`
              : 'Roster slot added.'
          );
        } else if (added && failed) {
          toast.warn(`Added ${added} day(s); ${failed} failed.`);
        } else {
          toast.error('Failed to add roster slot.');
        }
        this.getRoster();
      })
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  onRemoveSlot = (slot) => {
    const { doctorId, poolId } = this.props;
    this.setState({ saving: true });
    removeFromRoster(slot.rosterId, doctorId, poolId, slot)
      .then((res) => {
        if (res && res.message === 'success') {
          toast.success('Roster slot removed.');
          this.getRoster();
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to remove roster slot');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to remove roster slot');
      })
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  render() {
    const { roster, loading, error, saving, newDay, newStartHour, newEndHour } =
      this.state;
    const { poolName } = this.props;

    const slotsByDay = {};
    roster.forEach((slot) => {
      const day = parseInt(slot.day, 10);
      if (!slotsByDay[day]) {
        slotsByDay[day] = [];
      }
      slotsByDay[day].push(slot);
    });

    return (
      <Container>
        <BoldText>{`Weekly Roster${poolName ? ` — ${poolName}` : ''}`}</BoldText>
        {loading ? (
          <LoadingComponent />
        ) : error ? (
          <HelperText>{error}</HelperText>
        ) : (
          <React.Fragment>
            <WeekContainer>
              {WEEK_DAYS.map((d) => {
                const slots = slotsByDay[d.value] || [];
                return (
                  <DayRow key={d.value}>
                    <DayLabel>{d.display}</DayLabel>
                    <SlotsContainer>
                      {slots.length ? (
                        slots.map((slot) => (
                          <Slot key={slot.rosterId}>
                            {`${formatHour(slot.startHour)} - ${formatHour(
                              slot.endHour
                            )}`}
                            <RemoveSlotButton
                              title="Remove slot"
                              disabled={saving}
                              onClick={() => this.onRemoveSlot(slot)}
                            >
                              ✕
                            </RemoveSlotButton>
                          </Slot>
                        ))
                      ) : (
                        <EmptySlot>—</EmptySlot>
                      )}
                    </SlotsContainer>
                  </DayRow>
                );
              })}
            </WeekContainer>

            <AddForm>
              <Select value={newDay} onChange={this.onFieldChange('newDay')}>
                {DAY_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.display}
                  </option>
                ))}
              </Select>
              <Select
                value={newStartHour}
                onChange={this.onFieldChange('newStartHour')}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {formatHour(h)}
                  </option>
                ))}
              </Select>
              <RegularText color="#666">to</RegularText>
              <Select
                value={newEndHour}
                onChange={this.onFieldChange('newEndHour')}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {formatHour(h)}
                  </option>
                ))}
              </Select>
              <AddButton disabled={saving} onClick={this.onAddSlot}>
                {saving ? 'Saving…' : 'Add slot'}
              </AddButton>
            </AddForm>
          </React.Fragment>
        )}
      </Container>
    );
  }
}

export default PoolRosterView;
